import { useQuery } from '@tanstack/react-query';
import type { EngineDto, LaunchSiteDto, RocketDto } from '@trajectory/contracts';
import { apiFetch } from '@/lib/api';

export interface CelestialBodyDto {
  id: string;
  name: string;
  muM3S2: number;
  equatorialRadiusMeters: number;
  polarRadiusMeters: number;
  rotationPeriodSeconds: number;
  color: string;
  atmosphereColor: string | null;
  destination: boolean;
  description: string;
}

export interface ReferenceRocketDto {
  id: string;
  operator: string;
  country: string;
  firstFlight: number;
  publishedPayloadLeoKg: number;
  publishedPayloadGtoKg: number | null;
  launchCostUsd: number | null;
  description: string;
  vehicle: RocketDto;
}

export interface OrbitPresetDto {
  id: string;
  name: string;
  altitudeMeters: number;
  inclinationDeg: number | null;
  eccentricity: number;
  description: string;
}

/** Catalogue data changes only when the server is reseeded, so it can be cached generously. */
const CATALOG_STALE_TIME = 10 * 60_000;

export function useCatalogEngines() {
  return useQuery({
    queryKey: ['catalog', 'engines'],
    queryFn: () => apiFetch<EngineDto[]>('/catalog/engines'),
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCatalogLaunchSites() {
  return useQuery({
    queryKey: ['catalog', 'launch-sites'],
    queryFn: () => apiFetch<LaunchSiteDto[]>('/catalog/launch-sites'),
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCatalogBodies() {
  return useQuery({
    queryKey: ['catalog', 'bodies'],
    queryFn: () => apiFetch<CelestialBodyDto[]>('/catalog/bodies'),
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCatalogReferenceRockets() {
  return useQuery({
    queryKey: ['catalog', 'rockets'],
    queryFn: () => apiFetch<ReferenceRocketDto[]>('/catalog/rockets'),
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCatalogOrbitPresets() {
  return useQuery({
    queryKey: ['catalog', 'orbit-presets'],
    queryFn: () => apiFetch<OrbitPresetDto[]>('/catalog/orbit-presets'),
    staleTime: CATALOG_STALE_TIME,
  });
}
